import * as THREE from "three";

import { LANDMARK } from "./landmarks";

// Reused across frames; this runs 30-60x/second and allocating here shows up.
const pBridge = new THREE.Vector3();
const pLeft = new THREE.Vector3();
const pRight = new THREE.Vector3();
const pForehead = new THREE.Vector3();
const pChin = new THREE.Vector3();

const axisRight = new THREE.Vector3();
const axisUp = new THREE.Vector3();
const axisForward = new THREE.Vector3();
const basis = new THREE.Matrix4();

// ---- pitch correction ----
// MediaPipe's weak-perspective z values overstate how far the head tilts
// downward at steep angles (the chin z moves too much, the forehead z too
// little).  This inflates the forehead→chin axis and pushes the derived pitch
// past the true head angle, making temple tips float above the ears.
//
// At moderate angles (≤ ~20°) the z estimates are accurate enough to leave
// alone.  Beyond that we compress the pitch with a smoothstep that
// asymptotes to PITCH_CAP, keeping the arms within a plausible band.
const PITCH_SOFT_DEG = 20;
const PITCH_CAP_DEG = 38;
const PITCH_STALL_DEG = 60;
const PITCH_SOFT = THREE.MathUtils.degToRad(PITCH_SOFT_DEG);
const PITCH_CAP = THREE.MathUtils.degToRad(PITCH_CAP_DEG);
const PITCH_STALL = THREE.MathUtils.degToRad(PITCH_STALL_DEG);

const _euler = new THREE.Euler(0, 0, 0, "YXZ");

function compressPitch(pitch) {
  const sign = Math.sign(pitch);
  const magnitude = Math.abs(pitch);
  if (magnitude <= PITCH_SOFT) return pitch;
  const t = Math.min((magnitude - PITCH_SOFT) / (PITCH_STALL - PITCH_SOFT), 1);
  const eased = t * t * (3 - 2 * t);
  return sign * (PITCH_SOFT + (PITCH_CAP - PITCH_SOFT) * eased);
}

function correctPosePitch(q) {
  _euler.setFromQuaternion(q, "YXZ");
  _euler.x = compressPitch(_euler.x);
  q.setFromEuler(_euler);
}

/**
 * Derives head position, orientation and size from face landmarks.
 *
 * We build the pose ourselves rather than using MediaPipe's
 * `facialTransformationMatrixes`. That matrix is the obvious route, but it is
 * documented to drift when the face sits away from the centre of frame
 * (google-ai-edge/mediapipe#4759). Three landmark pairs give us a stable basis
 * with no such caveat.
 *
 * @param {import("@mediapipe/tasks-vision").NormalizedLandmark[]} landmarks
 * @param {ReturnType<import("./fit-mapping").createFitMapping>} mapping
 * @param {ReturnType<createEmptyPose>} target  Written in place; no allocation per frame.
 */
export function computeFacePose(landmarks, mapping, target) {
  if (landmarks.length < LANDMARK.FACE_RIGHT) return null;

  mapping.toWorld(landmarks[LANDMARK.NOSE_BRIDGE], pBridge);
  mapping.toWorld(landmarks[LANDMARK.FOREHEAD], pForehead);
  mapping.toWorld(landmarks[LANDMARK.CHIN], pChin);

  // Mirroring the preview flips which side of the screen each cheek lands on.
  // Swapping the two width landmarks keeps `right` pointing along the face's
  // own right — without this the basis becomes a reflection rather than a
  // rotation, and decomposing it yields a garbage quaternion.
  const leftIndex = mapping.mirrored ? LANDMARK.FACE_RIGHT : LANDMARK.FACE_LEFT;
  const rightIndex = mapping.mirrored ? LANDMARK.FACE_LEFT : LANDMARK.FACE_RIGHT;
  mapping.toWorld(landmarks[leftIndex], pLeft);
  mapping.toWorld(landmarks[rightIndex], pRight);

  const faceWidth = pLeft.distanceTo(pRight);
  if (!Number.isFinite(faceWidth) || faceWidth < 1e-3) return null;

  // Right is reliable; up from forehead->chin is only approximate because that
  // line is not perpendicular to it, so we use it to find forward and then
  // rebuild a true up. This is Gram-Schmidt: it guarantees an orthonormal,
  // right-handed basis whatever the head is doing.
  axisRight.subVectors(pRight, pLeft).normalize();
  axisUp.subVectors(pForehead, pChin).normalize();
  axisForward.crossVectors(axisRight, axisUp).normalize();
  axisUp.crossVectors(axisForward, axisRight).normalize();

  if (!Number.isFinite(axisForward.x) || axisForward.lengthSq() < 0.5) return null;

  basis.makeBasis(axisRight, axisUp, axisForward);

  target.position.copy(pBridge);
  target.quaternion.setFromRotationMatrix(basis);
  correctPosePitch(target.quaternion);
  target.faceWidth = faceWidth;
  return target;
}

export function createEmptyPose() {
  return {
    /** World position of the nose bridge, where the frame sits. */
    position: new THREE.Vector3(),
    /** Head orientation. */
    quaternion: new THREE.Quaternion(),
    /** Measured face width in world units — the frame scales to this. */
    faceWidth: 0,
  };
}
